import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '../types';
import { setAccessToken, clearAccessToken } from '../api/client';
import api from '../api/client';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  tenantSlug: string;
  login: (email: string, password: string, tenantSlug: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (...roles: string[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      tenantSlug: '',

      login: async (email, password, tenantSlug) => {
        const res = await api.post('/auth/login', { email, password, tenantSlug }, { timeout: 15000 });
        const { accessToken, user } = res.data.data;
        setAccessToken(accessToken);
        set({ user, isAuthenticated: true, tenantSlug });
      },

      logout: async () => {
        try { await api.post('/auth/logout'); } catch { /* ignore */ }
        clearAccessToken();
        set({ user: null, isAuthenticated: false, tenantSlug: '' });
      },

      refreshUser: async () => {
        const res = await api.get('/auth/me');
        set({ user: res.data.data });
      },

      hasPermission: (permission) => {
        const { user } = get();
        return user?.permissions?.includes(permission) ?? false;
      },

      hasRole: (...roles) => {
        const { user } = get();
        return roles.includes(user?.role ?? '');
      },
    }),
    {
      name: 'orbayx-auth',
      partialize: (state) => ({
        tenantSlug: state.tenantSlug,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
      }),
    },
  ),
);
